import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Lock, Clock, AlertTriangle, ShoppingBag, ShoppingCart, ArrowRight, LayoutDashboard, ScanLine, UserPlus, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function Login() {
  const { login, user, loading, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(() => new URLSearchParams(window.location.search).get("preview_admin") === "1");
  const isPreviewEnvironment = window.location.hostname === "localhost" || window.location.hostname.endsWith(".preview.emergentagent.com");

  const pending = params.get("pending") === "1";
  const errored = params.get("error") === "1";

  // Only redirect a fresh sign-in (?next flow) — otherwise let the user reach
  // the landing page from anywhere in the app.
  const authedNonPending = !loading && user && user.status !== "pending";

  const startGoogleAuth = async () => {
    try {
      await startGoogleLogin();
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err) {
      toast.error(err.message || "Sign-in failed. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back", { style: { pointerEvents: "none" } });
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : (err.message || "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lg:h-screen lg:overflow-hidden" data-testid="landing-page">
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 lg:origin-top-left lg:scale-[0.9] lg:w-[111.112%] lg:min-h-[111.112vh]">
      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="flex items-center gap-3">
            <img src="/brand/mpire-logo.png" alt="MPIRE Distribution" className="w-20 h-20 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.3)]" />
            <div>
              <div className="font-bold text-xl tracking-tight text-[var(--ink)]">MPower!</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500">
                Powered By{" "}
                <a href="https://2conmedia.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-700 hover:underline underline-offset-2">2ConMedia.com</a>
              </div>
            </div>
          </div>

          {/* Primary CTA — Shop With Us. Placed above Operations console to grab attention. */}
          <Link
            to="/shop"
            className="group flex items-center justify-center gap-2 w-full rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-black font-bold text-[1.3rem] tracking-tight px-6 py-4 shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
            data-testid="public-shop-link"
          >
            <ShoppingCart size={22} className="text-white" strokeWidth={2.25} />
            <span>Shop With Us</span>
          </Link>

          {/* Secondary CTA — Become A Client. Sits directly below Shop With Us. */}
          <Link
            to="/apply"
            className="group flex items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-gradient-to-br from-white to-[var(--brand)]/5 p-4 hover:border-[var(--brand)] hover:shadow-md transition-all"
            data-testid="public-apply-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[var(--brand)]/10 text-[var(--brand)] flex items-center justify-center shrink-0">
                <UserPlus size={18} />
              </div>
              <div>
                <p className="font-semibold text-sm text-[var(--ink)]">Become A Client</p>
                <p className="text-xs text-gray-500">Apply for wholesale access &amp; a private portal.</p>
                <p className="mt-1 text-[10px] uppercase tracking-widest text-amber-700 font-semibold" data-testid="apply-license-note">State Issued Cosmetology License Required</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-gray-400 group-hover:text-[var(--brand)] group-hover:translate-x-0.5 transition-all" />
          </Link>

          {/* Tertiary CTA — Become a Distributing Partner. */}
          <Link
            to="/apply-ep"
            className="group flex items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-gradient-to-br from-white to-amber-500/5 p-4 hover:border-amber-500 hover:shadow-md transition-all"
            data-testid="public-ep-apply-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
                <Truck size={18} />
              </div>
              <div>
                <p className="font-semibold text-sm text-[var(--ink)]">Become a Distributing Partner</p>
                <p className="text-xs text-gray-500">Distribute for MPIRE in your territory.</p>
                <p className="mt-1 text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Driver's License &amp; Insurance Required</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-gray-400 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all" />
          </Link>

          <div>
            <p className="label-eyebrow mb-3">MPire Personnel</p>
            <h1 className="text-4xl sm:text-5xl font-bold leading-none tracking-tight">Sign in.</h1>
            <p className="mt-3 text-gray-500">Onboarding, inventory, dispatch — all in one.</p>
          </div>

          {authedNonPending && (
            <div className="border border-emerald-300 bg-emerald-50 rounded-lg p-4 text-sm text-emerald-900 flex items-center justify-between gap-3" data-testid="already-signed-in-banner">
              <div>
                <p className="font-semibold">Signed in as {user.name}</p>
                <p className="text-emerald-700 text-xs mt-0.5">You can return to the operations app or sign in with a different account below.</p>
              </div>
              <Button
                type="button"
                onClick={() => navigate("/")}
                className="btn-brand h-9 shrink-0"
                data-testid="return-to-dashboard"
              >
                <LayoutDashboard size={14} className="mr-1.5" /> Dashboard
              </Button>
            </div>
          )}

          {pending && (
            <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 text-sm text-amber-900 flex gap-2" data-testid="pending-banner">
              <Clock size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Awaiting admin approval</p>
                <p className="text-amber-700 text-xs mt-1">Your sign-in was received. An MPower! admin will approve your account shortly. You'll be able to sign in again once approved.</p>
              </div>
            </div>
          )}
          {errored && !pending && (
            <div className="border border-red-300 bg-red-50 rounded-lg p-4 text-sm text-red-900 flex gap-2" data-testid="error-banner">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Sign-in failed</p>
                <p className="text-red-700 text-xs mt-1">Please try again or contact your MPower! admin.</p>
              </div>
            </div>
          )}

          {/* Continue with Google */}
          <div className="space-y-3">
            <Button
              type="button"
              onClick={startGoogleAuth}
              className="w-full h-11 bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 shadow-sm"
              data-testid="google-signin-button"
            >
              <GoogleIcon /> <span className="ml-2 font-semibold">Continue with Google</span>
            </Button>
            <Button
              type="button"
              onClick={() => navigate("/join")}
              className="w-full h-11 bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 shadow-sm"
              data-testid="email-phone-signin-button"
            >
              <Mail size={16} /> <span className="ml-2 font-semibold">Continue with Email/Phone</span>
            </Button>
            <p className="text-[11px] text-gray-400 text-center">
              @mpiredistribution.biz and select emails auto-approved. Others require admin approval.
            </p>
            {isPreviewEnvironment && (
              <Button type="button" variant="outline" onClick={(event) => { event.preventDefault(); event.stopPropagation(); setShowPasswordForm(true); navigate("/login?preview_admin=1", { replace: true }); }} className="relative z-30 w-full h-11 border-dashed border-[var(--brand)] text-[var(--brand)] hover:bg-[var(--brand)]/5" data-testid="preview-admin-login-button">
                <Lock size={16} className="mr-2" /> Preview Admin Login
              </Button>
            )}
          </div>

          {/* Password form is strictly preview/local-only. */}
          {isPreviewEnvironment && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-[10px] uppercase tracking-wider text-gray-400">
                  <span className="bg-white px-2">or admin fallback</span>
                </div>
              </div>

              {!showPasswordForm ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-10"
                  onClick={() => setShowPasswordForm(true)}
                  data-testid="show-password-login"
                >
                  <Lock size={14} className="mr-2" /> Password sign-in
                </Button>
              ) : (
                <form onSubmit={handleSubmit} className="w-full space-y-4" data-testid="login-form">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" data-testid="login-email-input" type="email"
                           value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" data-testid="login-password-input" type="password"
                           value={password} onChange={(e) => setPassword(e.target.value)} required />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full btn-brand"
                          data-testid="login-submit-button">
                    <Lock size={16} className="mr-2" /> {submitting ? "Signing in…" : "Sign in"}
                  </Button>
                </form>
              )}
            </>
          )}

          {/* Mobile-only QR code — for face-to-face customer encounters. Hidden on desktop (≥lg). */}
          <div className="lg:hidden pt-4" data-testid="mobile-shop-qr">
            <div className="rounded-2xl border-2 border-[var(--brand)]/30 bg-gradient-to-br from-white via-white to-[var(--brand)]/5 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <ScanLine size={16} className="text-[var(--brand)]" />
                <p className="label-eyebrow text-[var(--brand)]">Scan to shop</p>
              </div>
              <div className="flex items-center justify-center">
                <a
                  href={`${window.location.origin}/shop`}
                  className="shrink-0 rounded-xl bg-white p-3 border border-[var(--border)] shadow-sm active:scale-95 transition-transform"
                  data-testid="mobile-shop-qr-link"
                  aria-label="Open MPIRE shop"
                >
                  <QRCodeSVG
                    value={`${window.location.origin}/shop`}
                    size={160}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#B01E24"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img className="absolute inset-0 w-full h-full object-cover"
             src="https://images.pexels.com/photos/4562883/pexels-photo-4562883.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
             alt="Diverse warehouse team collaborating on logistics operations" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="label-eyebrow opacity-90">Field-aware dispatch</p>
          <h2 className="text-3xl font-bold leading-tight mt-2">From wholesale to last mile, in real time.</h2>
        </div>
      </div>
    </div>
    </div>
  );
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);
